
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";

interface CacheCoordinatorOptions {
  routineId: string;
}

export const useCacheCoordinator = ({ routineId }: CacheCoordinatorOptions) => {
  const queryClient = useQueryClient();
  const operationLockRef = useRef<Set<string>>(new Set());
  const dragReadyRef = useRef<boolean>(true);
  const debounceTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const isOperationLocked = useCallback(() => {
    return operationLockRef.current.size > 0;
  }, []);

  const isDragReady = useCallback(() => {
    return dragReadyRef.current;
  }, []);

  const lockOperation = useCallback((operationId: string) => {
    console.log(`🔒 Zero-refetch lock: ${operationId}`);
    operationLockRef.current.add(operationId);
    // Drag stays ready - no blocking
  }, []);

  const unlockOperation = useCallback((operationId: string) => {
    console.log(`🔓 Zero-refetch unlock: ${operationId}`);
    operationLockRef.current.delete(operationId);
    // Instant unlock - no timeouts
  }, []);

  // Smart invalidation with debouncing to prevent cascade refetches
  const invalidateExercises = useCallback(async (delay = 0, reason = "general") => {
    const debounceKey = `invalidate-${routineId}-${reason}`;
    
    // Cancel existing debounced invalidation
    const existingTimeout = debounceTimeoutsRef.current.get(debounceKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    const invalidateAction = async () => {
      console.log(`🔄 Smart cache invalidation for routine ${routineId} (${reason})`);
      await queryClient.invalidateQueries({
        queryKey: ["unified-exercises", routineId]
      });
      debounceTimeoutsRef.current.delete(debounceKey);
    };

    if (delay > 0) {
      const timeout = setTimeout(invalidateAction, delay);
      debounceTimeoutsRef.current.set(debounceKey, timeout);
    } else {
      await invalidateAction();
    }
  }, [queryClient, routineId]);

  // Optimized cache update with smart validation
  const updateExerciseCache = useCallback((exercises: any[]) => {
    console.log(`⚡ Zero-delay cache update with ${exercises.length} exercises`);
    
    // Validate data before updating cache
    if (!exercises || !Array.isArray(exercises)) {
      console.warn("⚠️ Invalid exercises data - skipping cache update");
      return;
    }
    
    // Smart update - only if data is valid
    queryClient.setQueryData(["unified-exercises", routineId], exercises);
  }, [queryClient, routineId]);

  // Cleanup function for component unmount
  const cleanup = useCallback(() => {
    debounceTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    debounceTimeoutsRef.current.clear();
  }, []);

  return {
    isOperationLocked,
    isDragReady,
    lockOperation,
    unlockOperation,
    invalidateExercises,
    updateExerciseCache,
    cleanup
  };
};
